import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'
import JSZip from 'jszip'

async function loadModelFromZip(file) {
  const zip = await JSZip.loadAsync(file)
  let modelFile, binFile

  zip.forEach((relativePath, zipEntry) => {
    const fileName = zipEntry.name.toLowerCase()
    if (
      (fileName.endsWith('.gltf') || fileName.endsWith('.glb')) &&
      !/^(__macosx)/.test(fileName)
    ) {
      modelFile = zipEntry
    } else if (fileName.endsWith('.bin')) {
      binFile = zipEntry
    } else if (fileName.endsWith('.fbx')) {
      modelFile = zipEntry
    }
  })

  if (!modelFile) {
    throw new Error('No model file found in the ZIP package')
  }

  const arrayBuffer = await modelFile.async('arraybuffer')
  const gltfFileContent = await modelFile.async('string')

  const fileType = modelFile.name.split('.').pop().toLowerCase()

  if (fileType === 'gltf') {
    return loadGLTF(gltfFileContent, binFile)
  } else if (fileType === 'glb') {
    return loadGLTF(arrayBuffer)
  } else if (fileType === 'fbx') {
    return loadFBX(arrayBuffer)
  }
  throw new Error('Unsupported model file format in ZIP package')
}

function loadGLTF(gltfContent, binFile = null) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader()

    if (binFile) {
      binFile
        .async('arraybuffer')
        .then((binArrayBuffer) => {
          const binBlobURL = URL.createObjectURL(new Blob([binArrayBuffer]))

          const gltfJson = JSON.parse(gltfContent)
          gltfJson.buffers.forEach((buffer) => {
            if (buffer.uri.toLowerCase().endswith('.bin')) {
              buffer.uri = binBlobURL
            }
          })

          const modifiedGltf = JSON.stringify(gltfJson)

          loader.parse(
            modifiedGltf,
            '',
            (gltf) => {
              URL.revokeObjectURL(binBlobURL)
              const modelInfo = extractModelInfo(gltf.scene)
              resolve(modelInfo)
            },
            reject,
          )
        })
        .catch(reject)
    } else {
      loader.parse(
        gltfContent,
        '',
        (gltf) => {
          const modelInfo = extractModelInfo(gltf.scene)
          resolve(modelInfo)
        },
        reject,
      )
    }
  })
}

function loadFBX(arrayBuffer) {
  return new Promise((resolve, reject) => {
    const loader = new FBXLoader()
    try {
      const object = loader.parse(arrayBuffer)
      const modelInfo = extractModelInfo(object)
      resolve(modelInfo)
    } catch (error) {
      reject(error)
    }
  })
}

// 递归组装骨骼的完整路径
function getBoneNameWithPath(bone: any): string {
  // 如果对象没有父级，则它就是根骨骼节点
  if (!bone.parent) {
    return bone.name
  } else if (bone.parent.type !== 'Bone') {
    // return bone.parent.name +'/'+bone.name;
    return bone.name
  }

  // 如果对象有父级，则继续向上遍历父级，直到找到没有父级的对象
  return getBoneNameWithPath(bone.parent) + '/' + bone.name
}

function extractModelInfo(object) {
  let totalVertices = 0
  let totalTriangles = 0
  const meshList = []
  let materialCount = 0 // 材质数量
  let skinCount = 0 // 蒙皮数量
  let has_vertices = false // 顶点
  let has_normals = false // 法线
  let has_UVs = false // 纹理坐标
  // 初始化骨骼数量和骨骼名称数组
  let boneCount = 0
  const boneNames: string[] = []

  const uniqueVertices = new Set()

  object.traverse((child) => {
    if (child.isMesh) {
      const geometry = child.geometry
      const indices = geometry.index
        ? geometry.index.count
        : geometry.attributes.position.count

      // 计算实际的三角形数
      totalTriangles += indices / 3

      // 计算唯一顶点
      const positionArray = geometry.attributes.position.array
      for (let i = 0; i < positionArray.length; i += 3) {
        const vertexKey = `${positionArray[i]},${positionArray[i + 1]},${
          positionArray[i + 2]
        }`
        uniqueVertices.add(vertexKey)
      }

      const attributes = geometry.attributes
      if (attributes) {
        has_vertices = attributes.position !== undefined
        has_normals = attributes.normal !== undefined
        has_UVs = attributes.uv !== undefined
      }

      meshList.push(child.name || 'Unnamed Mesh')

      // 累加材质数量
      if (child.material) {
        if (Array.isArray(child.material)) {
          materialCount += child.material.length
        } else {
          materialCount++
        }
      }

      // 如果该网格有蒙皮
      if (child.skeleton) {
        skinCount++
      }
    } else {
      if (child.isBone) {
        boneCount++
        boneNames.push(getBoneNameWithPath(child))
      }
    }
  })

  totalVertices = uniqueVertices.size

  return {
    vertices: totalVertices,
    triangles: totalTriangles,
    meshes: meshList,
    material_count: materialCount,
    skin_count: skinCount,
    skeleton_names: boneNames,
    skeleton_count: boneCount,
    has_vertices,
    has_normals,
    has_UVs,
  }
}

export { loadModelFromZip }
